import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { animated } from 'react-spring';
import { AppCriteriaField, AppSkinProps } from '../../types/decisions';
import Image from '../image/Image';
import linesBackground from './linesBackground2.png';

import './draggable.scss';
import { useSearchParams } from 'react-router-dom';
import NameWithIcon from '../application/app-components/NameWithIcon/NameWithIcon';

interface CriteriaItem extends AppCriteriaField {
    backgroundColor: string;
    angle: number;
    translateY: number;
    translateYOffset: number;
    name: string;
    flipped: boolean;
}

interface Props {
    initialCriteriaFields: AppCriteriaField[];
    onCriteriaApply?: AppSkinProps['onCriteriaApply'];
    onSliderChange: AppSkinProps['onSliderChange'];
    onCriteriaSettingsOpen?: (id: string) => void;
    onAutoPopupCheck?: (newImportance: number, id: string) => void;
    isEditable?: boolean;
}

const colors = [
    '#2E8B57',
    '#1E90FF',
    '#FF6347',
    '#4B0082',
    '#FF4500',
    '#FFB6C1',
    '#3498db',
    '#6B8E23',
    '#800080',
    '#34495E',
];

const CARDS_PER_PAGE = 5;

const anglesAndOffsets: {
    [key: number]: {
        angles: number[],
        offsets: number[]
    }
} = {
    5: {
        angles: [-18, -9, 0, 9, 18],
        offsets: [46, 3, -12, 3, 46],
    },
    4: {
        angles: [-15, -5, 5, 15],
        offsets: [20, -10, -10, 20],
    },
    3: {
        angles: [-10, 0, 10],
        offsets: [1, -14, 1],
    },
    2: {
        angles: [-5, 5],
        offsets: [-10, -10],
    },
    1: {
        angles: [0],
        offsets: [-14],
    },
};

const generateCriteriaInitialInfo = (initialCriteriaFields: AppCriteriaField[]): CriteriaItem[] => {
    const visibleItems = initialCriteriaFields.filter(item => !item.hide_importance);
    const maxItemsCount = Math.min(visibleItems.length, 5);

    return visibleItems.map((item, index) => {
        return {
            ...item,
            backgroundColor: colors[index % colors.length],
            angle: anglesAndOffsets[maxItemsCount].angles[index % CARDS_PER_PAGE],
            name: typeof item.name === 'object' ? item.name.value : item.name,
            translateYOffset: anglesAndOffsets[maxItemsCount].offsets[index % CARDS_PER_PAGE],
            translateY: Math.round(-item.importance * 1.1),
            flipped: false,
        };
    });
};

function getFontSize(textLength: number): number {
    if (textLength < 16) return 18;
    if (textLength < 19) return 16;
    if (textLength < 23) return 14;
    return 12;
}

const DraggableDiv = ({
    initialCriteriaFields,
    onSliderChange,
    onCriteriaApply,
    onCriteriaSettingsOpen,
    onAutoPopupCheck,
    isEditable = true,
}: Props) => {
    const [searchParams] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';

    const [dragging, setDragging] = useState(false);
    const [criteria, setCriteria] = useState<CriteriaItem[]>([]);
    const [criteriaFirstIndex, setCriteriaFirstIndex] = useState(0);

    const selectedCardIdRef = useRef<string | null>(null);
    const startMouseYRef = useRef(0);
    const startTranslateYRef = useRef(0);

    const maxDragUp = -110;
    const minDragDown = 0;

    useEffect(() => {
        setCriteria(generateCriteriaInitialInfo(initialCriteriaFields));
    }, [initialCriteriaFields]);

    const startDraggingCard = (
        e: React.MouseEvent<HTMLDivElement>,
        criteriaItem: CriteriaItem
    ) => {
        if (!isEditable) return;

        e.stopPropagation();
        selectedCardIdRef.current = criteriaItem.id;
        startMouseYRef.current = e.clientY;
        startTranslateYRef.current = criteriaItem.translateY;
        setDragging(true);
    };

    useEffect(() => {
        if (!dragging || !isEditable) return;

        const handleMouseMove = (event: MouseEvent) => {
            if (selectedCardIdRef.current === null) return;

            const deltaY = event.clientY - startMouseYRef.current;
            const nextTranslateY = Math.max(
                maxDragUp,
                Math.min(minDragDown, startTranslateYRef.current + deltaY)
            );

            setCriteria(prev =>
                prev.map(item => {
                    if (item.id === selectedCardIdRef.current) {
                        return {
                            ...item,
                            translateY: nextTranslateY,
                            importance: Math.min(
                                100,
                                Math.max(0, Math.round(Math.abs(nextTranslateY) / 1.1))
                            ),
                        };
                    }

                    return item;
                })
            );
        };

        const handleMouseUp = () => {
            setDragging(false);

            const selectedId = selectedCardIdRef.current;
            if (!selectedId) return;

            const currentItem = criteria.find(item => item.id === selectedId);
            if (currentItem) {
                const newImportance = Math.min(
                    100,
                    Math.max(0, Math.round(Math.abs(currentItem.translateY) / 1.1))
                );

                onSliderChange(newImportance, selectedId);
                onAutoPopupCheck?.(newImportance, selectedId);
            }

            selectedCardIdRef.current = null;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, maxDragUp , minDragDown, isEditable, criteria, onSliderChange, onAutoPopupCheck]);

    const onOpenSettings = (id: string) => {
        if (!isEditable) return;
        onCriteriaSettingsOpen?.(id);
    };

    const criteriaToShow = criteria.slice(criteriaFirstIndex, criteriaFirstIndex + CARDS_PER_PAGE);
    const anglesAnfOffsetsIndex = criteriaToShow.length || 5;
    const { angles, offsets } = anglesAndOffsets[anglesAnfOffsetsIndex];

    return (
        <>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    backgroundImage: `url(${linesBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    userSelect: 'none',
                }}
            >
                {criteriaToShow.map((criteriaItem, index) => {
                    const isSelected = criteriaItem.id === selectedCardIdRef.current;
                    const transformRotate = `rotate(${angles[index]}deg)`;
                    const transformY = `translateY(${criteriaItem.translateY + offsets[index]}px)`;
                    const transformValues = `${transformRotate} ${transformY}`;

                    return (
                        <animated.div
                            key={criteriaItem.id}
                            onMouseDown={(e) => startDraggingCard(e, criteriaItem)}
                            className="app-criteria-card-item"
                            style={{
                                outline: isSelected ? '2px solid black' : '1px solid grey',
                                cursor: isEditable ? 'grab' : 'default',
                                transform: transformValues,
                                backgroundColor: 'white',
                                zIndex: isSelected ? 2 : 1,
                                position: 'relative',
                            }}
                        >
                            <div
                                className="importance-bar"
                                style={{ width: `${criteriaItem.importance}%` }}
                            />
                            <h3
                                className="importance-card-title"
                                style={{
                                    fontSize: getFontSize(criteriaItem.name.length),
                                }}
                            >
                                <NameWithIcon 
                                    icon={criteriaItem.icon}
                                    name={criteriaItem.name}
                                    link={criteriaItem.link}
                                /> 
                                {!!onCriteriaSettingsOpen && (
                                    <IconButton
                                        style={{ marginLeft: 8 }}
                                        onClick={() => onOpenSettings(criteriaItem.id)}
                                    >
                                        <EditIcon color="primary" fontSize="small" />
                                    </IconButton>
                                )}
                            </h3>
                            <Image src={criteriaItem.icon} className="app-criteria-icon" />
                        </animated.div>
                    );
                })}
            </div>

            {isEditable && (
                <div className="draggable-div-actions">
                    <Button
                        variant="outlined"
                        disabled={criteriaFirstIndex === 0}
                        onClick={() => setCriteriaFirstIndex(p => p - 1)}
                    >
                        <ChevronLeftIcon />
                    </Button>

                    {!!onCriteriaApply ? (
                        <Button
                            variant="contained"
                            className="decide-btn"
                            onClick={() => onCriteriaApply(dataset)}
                        >
                            Decide
                        </Button>
                    ) : (
                        <>&nbsp;</>
                    )}

                    <Button
                        variant="outlined"
                        disabled={criteriaFirstIndex >= criteria.length - CARDS_PER_PAGE}
                        onClick={() => setCriteriaFirstIndex(p => p + 1)}
                    >
                        <ChevronRightIcon />
                    </Button>
                </div>
            )}
        </>
    );
};

export default DraggableDiv;